import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Settings as SettingsIcon, 
  Save, 
  Phone, 
  CreditCard,
  Shield,
  Database,
  Bell
} from 'lucide-react';
import { getApiSettings, saveApiSettings, loadApiSettings } from '../services/api';

const SettingsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SettingsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 30px;
`;

const SettingsCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const InfoText = styled.p`
  color: #666;
  font-size: 14px;
  margin-top: 5px;
  line-height: 1.5;
`;

const StatusIndicator = styled.div<{ status: 'active' | 'inactive' | 'error' }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => 
    props.status === 'active' ? '#d4edda' : 
    props.status === 'error' ? '#f8d7da' : '#fff3cd'
  };
  color: ${props => 
    props.status === 'active' ? '#155724' : 
    props.status === 'error' ? '#721c24' : '#856404'
  };
`;

const TestButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #667eea;
  border-radius: 6px;
  background: transparent;
  color: #667eea;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #667eea;
    color: white;
  }
`;

const Settings: React.FC = () => {
  const [settings, setSettings] = useState({
    whatsappApiUrl: 'http://login.click4me.co.il:3000/hook',
    smsApiUrl: 'http://login.click4me.co.il:9797/http/send-message',
    emailApiUrl: '/email/send',
    smsUsername: 'admin',
    smsPassword: 'eran4ever',
    iCountApiKey: '',
    iCountApiUrl: '',
    // Email SMTP Settings
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: 'זכרון דליה',
    synagogueName: 'זכרון דליה',
    synagogueAddress: '',
    synagoguePhone: '',
    adminPhone: '',
    reminderDays: 7,
    autoReminders: true
  });

  const [testing, setTesting] = useState({
    whatsapp: false,
    sms: false,
    email: false,
    iCount: false
  });

  const [testResults, setTestResults] = useState({
    whatsapp: null as boolean | null,
    sms: null as boolean | null,
    email: null as boolean | null,
    iCount: null as boolean | null
  });

  useEffect(() => {
    // Load settings from database
    const loadSettings = async () => {
      try {
        await loadApiSettings();
        const currentSettings = getApiSettings();
        setSettings(prev => ({
          ...prev,
          whatsappApiUrl: currentSettings.whatsappApiUrl,
          smsApiUrl: currentSettings.smsApiUrl,
          emailApiUrl: currentSettings.emailApiUrl,
          smsUsername: currentSettings.smsUsername,
          smsPassword: currentSettings.smsPassword,
          iCountApiKey: currentSettings.iCountApiKey,
          iCountApiUrl: currentSettings.iCountApiUrl,
          // Email SMTP Settings
          smtpHost: currentSettings.smtpHost,
          smtpPort: currentSettings.smtpPort,
          smtpSecure: currentSettings.smtpSecure,
          smtpUser: currentSettings.smtpUser,
          smtpPassword: currentSettings.smtpPassword,
          smtpFromEmail: currentSettings.smtpFromEmail,
          smtpFromName: currentSettings.smtpFromName
        }));
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  const handleSaveSettings = async () => {
    try {
      const apiSettings = {
        whatsappApiUrl: settings.whatsappApiUrl,
        smsApiUrl: settings.smsApiUrl,
        emailApiUrl: settings.emailApiUrl,
        smsUsername: settings.smsUsername,
        smsPassword: settings.smsPassword,
        iCountApiKey: settings.iCountApiKey,
        iCountApiUrl: settings.iCountApiUrl,
        // Email SMTP Settings
        smtpHost: settings.smtpHost,
        smtpPort: settings.smtpPort,
        smtpSecure: settings.smtpSecure,
        smtpUser: settings.smtpUser,
        smtpPassword: settings.smtpPassword,
        smtpFromEmail: settings.smtpFromEmail,
        smtpFromName: settings.smtpFromName
      };
      
      await saveApiSettings(apiSettings);
      alert('ההגדרות נשמרו בהצלחה');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('שגיאה בשמירת ההגדרות');
    }
  };

  const testWhatsApp = async () => {
    setTesting(prev => ({ ...prev, whatsapp: true }));
    try {
      // Test WhatsApp API
      const response = await fetch(`${settings.whatsappApiUrl}?body=בדיקת API&phone=972501234567`);
      const success = response.ok;
      setTestResults(prev => ({ ...prev, whatsapp: success }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, whatsapp: false }));
    } finally {
      setTesting(prev => ({ ...prev, whatsapp: false }));
    }
  };

  const testSMS = async () => {
    setTesting(prev => ({ ...prev, sms: true }));
    try {
      // Test SMS API
      const response = await fetch(`${settings.smsApiUrl}?username=${settings.smsUsername}&password=${settings.smsPassword}&to=972501234567&message-type=sms.automatic&message=בדיקת API`);
      const success = response.ok;
      setTestResults(prev => ({ ...prev, sms: success }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, sms: false }));
    } finally {
      setTesting(prev => ({ ...prev, sms: false }));
    }
  };

  const testEmail = async () => {
    setTesting(prev => ({ ...prev, email: true }));
    try {
      // Test Email API
      const response = await fetch(settings.emailApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: settings.smtpFromEmail || 'test@example.com',
          subject: 'בדיקת API - זכרון דליה',
          message: 'זוהי הודעת בדיקה למערכת האימיילים',
          smtpSettings: {
            host: settings.smtpHost,
            port: settings.smtpPort,
            secure: settings.smtpSecure,
            auth: {
              user: settings.smtpUser,
              pass: settings.smtpPassword
            },
            from: `"${settings.smtpFromName}" <${settings.smtpFromEmail}>`
          }
        })
      });
      const success = response.ok;
      setTestResults(prev => ({ ...prev, email: success }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, email: false }));
    } finally {
      setTesting(prev => ({ ...prev, email: false }));
    }
  };

  const testICount = async () => {
    setTesting(prev => ({ ...prev, iCount: true }));
    try {
      // Test iCount API
      const response = await fetch(`${settings.iCountApiUrl}/api/test`, {
        headers: {
          'Authorization': `Bearer ${settings.iCountApiKey}`,
          'Content-Type': 'application/json'
        }
      });
      const success = response.ok;
      setTestResults(prev => ({ ...prev, iCount: success }));
    } catch (error) {
      setTestResults(prev => ({ ...prev, iCount: false }));
    } finally {
      setTesting(prev => ({ ...prev, iCount: false }));
    }
  };

  const getStatusText = (type: 'whatsapp' | 'sms' | 'email' | 'iCount') => {
    if (testResults[type] === null) return 'לא נבדק';
    return testResults[type] ? 'פעיל' : 'שגיאה';
  };

  const getStatusType = (type: 'whatsapp' | 'sms' | 'email' | 'iCount') => {
    if (testResults[type] === null) return 'inactive';
    return testResults[type] ? 'active' : 'error';
  };

  return (
    <SettingsContainer>
      <Header>
        <Title>
          <SettingsIcon size={28} />
          הגדרות מערכת
        </Title>
      </Header>

      <SettingsGrid>
        <SettingsCard>
          <CardTitle>
            <Phone size={20} />
            הגדרות SMS
          </CardTitle>

          <FormGroup>
            <Label>כתובת API של SMS</Label>
            <Input
              type="url"
              value={settings.smsApiUrl}
              onChange={(e) => setSettings({ ...settings, smsApiUrl: e.target.value })}
              placeholder="http://login.click4me.co.il:9797/http/send-message"
            />
          </FormGroup>

          <FormGroup>
            <Label>שם משתמש</Label>
            <Input
              type="text"
              value={settings.smsUsername}
              onChange={(e) => setSettings({ ...settings, smsUsername: e.target.value })}
              placeholder="admin"
            />
          </FormGroup>

          <FormGroup>
            <Label>סיסמה</Label>
            <Input
              type="password"
              value={settings.smsPassword}
              onChange={(e) => setSettings({ ...settings, smsPassword: e.target.value })}
              placeholder="eran4ever"
            />
          </FormGroup>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
            <TestButton onClick={testSMS} disabled={testing.sms}>
              {testing.sms ? 'בודק...' : 'בדוק חיבור'}
            </TestButton>
            <StatusIndicator status={getStatusType('sms')}>
              {getStatusText('sms')}
            </StatusIndicator>
          </div>
        </SettingsCard>

        <SettingsCard>
          <CardTitle>
            <Bell size={20} />
            הגדרות WhatsApp
          </CardTitle>

          <FormGroup>
            <Label>כתובת API של WhatsApp</Label>
            <Input
              type="url"
              value={settings.whatsappApiUrl}
              onChange={(e) => setSettings({ ...settings, whatsappApiUrl: e.target.value })}
              placeholder="http://login.click4me.co.il:3000/hook"
            />
          </FormGroup>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
            <TestButton onClick={testWhatsApp} disabled={testing.whatsapp}>
              {testing.whatsapp ? 'בודק...' : 'בדוק חיבור'}
            </TestButton>
            <StatusIndicator status={getStatusType('whatsapp')}>
              {getStatusText('whatsapp')}
            </StatusIndicator>
          </div>
        </SettingsCard>

        <SettingsCard>
          <CardTitle>
            <Bell size={20} />
            הגדרות אימייל (SMTP)
          </CardTitle>

          <FormGroup>
            <Label>שרת SMTP</Label>
            <Input
              type="text"
              value={settings.smtpHost}
              onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
              placeholder="smtp.gmail.com"
            />
          </FormGroup>

          <FormGroup>
            <Label>פורט</Label>
            <Input
              type="number"
              value={settings.smtpPort}
              onChange={(e) => setSettings({ ...settings, smtpPort: parseInt(e.target.value) })}
              placeholder="587"
            />
          </FormGroup>

          <FormGroup>
            <Label>אימייל משתמש</Label>
            <Input
              type="email"
              value={settings.smtpUser}
              onChange={(e) => setSettings({ ...settings, smtpUser: e.target.value })}
              placeholder="your-email@gmail.com"
            />
          </FormGroup>

          <FormGroup>
            <Label>סיסמת אפליקציה</Label>
            <Input
              type="password"
              value={settings.smtpPassword}
              onChange={(e) => setSettings({ ...settings, smtpPassword: e.target.value })}
              placeholder="App Password (לא הסיסמה הרגילה)"
            />
          </FormGroup>

          <FormGroup>
            <Label>אימייל שולח</Label>
            <Input
              type="email"
              value={settings.smtpFromEmail}
              onChange={(e) => setSettings({ ...settings, smtpFromEmail: e.target.value })}
              placeholder="synagogue@domain.com"
            />
          </FormGroup>

          <FormGroup>
            <Label>שם שולח</Label>
            <Input
              type="text"
              value={settings.smtpFromName}
              onChange={(e) => setSettings({ ...settings, smtpFromName: e.target.value })}
              placeholder="זכרון דליה"
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={settings.smtpSecure}
                onChange={(e) => setSettings({ ...settings, smtpSecure: e.target.checked })}
                style={{ marginLeft: '8px' }}
              />
              חיבור מאובטח (SSL/TLS)
            </Label>
          </FormGroup>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
            <TestButton onClick={testEmail} disabled={testing.email}>
              {testing.email ? 'בודק...' : 'בדוק חיבור'}
            </TestButton>
            <StatusIndicator status={getStatusType('email')}>
              {getStatusText('email')}
            </StatusIndicator>
          </div>
        </SettingsCard>

        <SettingsCard>
          <CardTitle>
            <CreditCard size={20} />
            הגדרות iCount
          </CardTitle>

          <FormGroup>
            <Label>כתובת API של iCount</Label>
            <Input
              type="url"
              value={settings.iCountApiUrl}
              onChange={(e) => setSettings({ ...settings, iCountApiUrl: e.target.value })}
              placeholder="https://api.icount.co.il"
            />
          </FormGroup>

          <FormGroup>
            <Label>מפתח API</Label>
            <Input
              type="password"
              value={settings.iCountApiKey}
              onChange={(e) => setSettings({ ...settings, iCountApiKey: e.target.value })}
              placeholder="הכנס מפתח API"
            />
          </FormGroup>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '15px' }}>
            <TestButton onClick={testICount} disabled={testing.iCount}>
              {testing.iCount ? 'בודק...' : 'בדוק חיבור'}
            </TestButton>
            <StatusIndicator status={getStatusType('iCount')}>
              {getStatusText('iCount')}
            </StatusIndicator>
          </div>
        </SettingsCard>

        <SettingsCard>
          <CardTitle>
            <Shield size={20} />
            פרטי בית כנסת
          </CardTitle>

          <FormGroup>
            <Label>שם בית הכנסת</Label>
            <Input
              type="text"
              value={settings.synagogueName}
              onChange={(e) => setSettings({ ...settings, synagogueName: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>כתובת</Label>
            <TextArea
              value={settings.synagogueAddress}
              onChange={(e) => setSettings({ ...settings, synagogueAddress: e.target.value })}
              placeholder="הכנס את כתובת בית הכנסת"
            />
          </FormGroup>

          <FormGroup>
            <Label>טלפון</Label>
            <Input
              type="tel"
              value={settings.synagoguePhone}
              onChange={(e) => setSettings({ ...settings, synagoguePhone: e.target.value })}
              placeholder="02-1234567"
            />
          </FormGroup>

          <FormGroup>
            <Label>טלפון מנהל</Label>
            <Input
              type="tel"
              value={settings.adminPhone}
              onChange={(e) => setSettings({ ...settings, adminPhone: e.target.value })}
              placeholder="050-1234567"
            />
          </FormGroup>
        </SettingsCard>

        <SettingsCard>
          <CardTitle>
            <Bell size={20} />
            הגדרות תזכורות
          </CardTitle>

          <FormGroup>
            <Label>מספר ימים לפני תזכורת</Label>
            <Input
              type="number"
              value={settings.reminderDays}
              onChange={(e) => setSettings({ ...settings, reminderDays: Number(e.target.value) })}
              min="1"
              max="30"
            />
            <InfoText>המערכת תשלח תזכורת X ימים לפני תאריך היעד של החוב</InfoText>
          </FormGroup>

          <FormGroup>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={settings.autoReminders}
                onChange={(e) => setSettings({ ...settings, autoReminders: e.target.checked })}
                style={{ width: '18px', height: '18px', accentColor: '#667eea' }}
              />
              <Label style={{ margin: 0 }}>שלח תזכורות אוטומטיות</Label>
            </div>
            <InfoText>המערכת תשלח תזכורות אוטומטיות למתפללים עם חובות פתוחים</InfoText>
          </FormGroup>
        </SettingsCard>

        <SettingsCard>
          <CardTitle>
            <Database size={20} />
            הגדרות מסד נתונים
          </CardTitle>

          <FormGroup>
            <Label>גיבוי נתונים</Label>
            <Button variant="secondary" onClick={() => {
              // Implement backup functionality
              alert('פונקציית הגיבוי תהיה זמינה בקרוב');
            }}>
              <Database size={16} />
              יצור גיבוי
            </Button>
            <InfoText>צור גיבוי של כל הנתונים במערכת</InfoText>
          </FormGroup>

          <FormGroup>
            <Label>שחזור נתונים</Label>
            <Button variant="secondary" onClick={() => {
              // Implement restore functionality
              alert('פונקציית השחזור תהיה זמינה בקרוב');
            }}>
              <Database size={16} />
              שחזר מגיבוי
            </Button>
            <InfoText>שחזר נתונים מגיבוי קיים</InfoText>
          </FormGroup>
        </SettingsCard>
      </SettingsGrid>

      <ButtonGroup>
        <Button variant="primary" onClick={handleSaveSettings}>
          <Save size={16} />
          שמור הגדרות
        </Button>
      </ButtonGroup>
    </SettingsContainer>
  );
};

export default Settings;
